import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import Drawer from "@mui/material/Drawer";
import { TendbeLogo, UserContainer, UserName } from "./MainMenu.styles";
import { LoginActions } from "@Pages";
import { useDispatch, useSelector } from "react-redux";
import { AuthSelectors, RootStateType } from "@Store";
import { Divider } from "@Styled/Pages/Login";
import { ChannelContainer } from "@Components";
import AppBar from "@mui/material/AppBar";
import UserAvatar from "../PollItem/UserAvatar";
import useRestrictions from "@Hooks/useRestrictions";
import { useTranslation } from "react-i18next";
import { hasPremiumFeatures } from "@Utils/accountEntitlements";

export const MainMenu = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openSideBar, setOpenSideBar] = useState<boolean>(false);
    const [accountType] = useRestrictions();
    const open = Boolean(anchorEl);
    const isAuthUser = AuthSelectors.selectIsUserAuthorized();
    const theme = useTheme();

    const user = useSelector((state: RootStateType) => state.auth.user);
    const userName = user.username;

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogOut = () => {
        dispatch(LoginActions.Logout());
    };

    const handleProfileSettingsOpen = () => {
        setAnchorEl(null);
        navigate(`/profile-settings`);
    };

    const toggleDrawer =
        (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
            if (
                event.type === "keydown" &&
                ((event as React.KeyboardEvent).key === "Tab" ||
                    (event as React.KeyboardEvent).key === "Shift")
            ) {
                return;
            }
            setOpenSideBar(open);
        };

    return (
        <>
            <AppBar
                position="static"
                sx={{
                    background: "url('/images/background.png')",
                    backgroundPosition: "left center",
                    backgroundSize: "260%",
                    backgroundRepeat: "no-repeat",
                    minHeight: "80px",
                }}
            >
                <Toolbar
                    variant="dense"
                    sx={{
                        flex: 1,
                        justifyContent: isAuthUser ? "space-between" : "center",
                        [theme.breakpoints.up(768)]: {
                            maxWidth: "1025px",
                            margin: "auto",
                            width: "100%",
                            padding: 0,
                        },
                    }}
                >
                    {isAuthUser && (
                        <Box
                            sx={{
                                display: "block",
                                [theme.breakpoints.up(768)]: {
                                    display: "none",
                                },
                            }}
                        >
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                onClick={toggleDrawer(true)}
                                sx={{ mr: 2 }}
                                size="large"
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>

                            <Drawer
                                anchor="left"
                                open={openSideBar}
                                onClose={toggleDrawer(false)}
                                PaperProps={{
                                    sx: {
                                        width: "100%",
                                        [theme.breakpoints.up("sm")]: {
                                            width: "auto",
                                        },
                                    },
                                }}
                            >
                                <List sx={{ minWidth: "220px" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <IconButton
                                            onClick={toggleDrawer(false)}
                                            size="large"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                    <ChannelContainer
                                        expandParentsByDefault
                                        fetchChannelsOnMount
                                        onChannelNavigate={() =>
                                            setOpenSideBar(false)
                                        }
                                        showChannelMenu={false}
                                        showChannelAvatar={false}
                                        showInlineSubChannelAction
                                    />
                                </List>
                            </Drawer>
                        </Box>
                    )}

                    <TendbeLogo href="https://app.tendbe.com" />

                    {isAuthUser && (
                        <Box sx={{ pt: "0.5rem" }}>
                            <IconButton
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                color="inherit"
                                size="large"
                            >
                                <UserContainer>
                                    <UserAvatar
                                        username={user.username}
                                        avatar={user.avatar}
                                        size="small"
                                    />
                                    <UserName>
                                        {hasPremiumFeatures(accountType) && (
                                            <span
                                                role="img"
                                                aria-label="premium"
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginRight:
                                                        theme.spacing(0.5),
                                                    fontSize: "12px",
                                                    lineHeight: 1,
                                                    verticalAlign: "middle",
                                                }}
                                            >
                                                ⭐️
                                            </span>
                                        )}
                                        {userName}
                                    </UserName>
                                </UserContainer>
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                open={open}
                                onClose={handleClose}
                            >
                                <MenuItem onClick={handleProfileSettingsOpen}>
                                    {t("mainMenu.profile")}
                                </MenuItem>
                                <MenuItem onClick={handleLogOut}>
                                    {t("mainMenu.logout")}
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            <Divider></Divider>
        </>
    );
};
